/**
 * What each criterion looks like as a control. [IT-707 P2.2]
 *
 * capabilities.js answers "can this surface apply this criterion, and under what
 * param name". This answers "what does the operator see". Keeping them apart is
 * deliberate: capability is a property of the backend surface, presentation is
 * not, and the two drifted for years precisely because each of three components
 * hardcoded both at once.
 *
 * Adding a filter is now: one CRITERIA entry, one serialiser line, one contract
 * entry, one FIELDS entry. Four places, all guarded. IT-791 added Question ID
 * under the old shape and it took thirteen hand-edited files, four of which were
 * wrong until the audit harness reported them.
 *
 * PLACEMENT still belongs to the view: a page that owns a criterion in its own
 * state (GroupCountQC's review toggles, BoxTagging's QC checkboxes) passes it in
 * `pageLevel` and the filter neither renders nor emits it.
 */

import contract from './filter-contract.json'
import {CRITERIA, SURFACES, supportedCriteria} from './capabilities'

/** capabilities surface key -> filter-contract.json surface key. */
const CONTRACT_KEY = {
  [SURFACES.PHOTO_TAGGING]: 'photo-tagging',
  [SURFACES.PHOTO_QC]: 'photo-qc',
  [SURFACES.GROUP_TAGGING]: 'group-tagging',
  [SURFACES.GROUP_QC]: 'group-qc',
}

const DATE_NOUN = {
  date_acquired: 'Acquired',
  date_tagged: 'Tagged',
}

/**
 * Which date this surface's range actually narrows.
 *
 * Read from the contract rather than written down twice. GroupQCFilterModal
 * labelled both its inputs "Tagged Date" while apply_group_filters filters
 * date_acquired — a label that was simply false, and the kind of thing a
 * reviewer cannot catch by reading the component alone.
 */
export function dateNoun(surface) {
  const declared = contract.surfaces[CONTRACT_KEY[surface]]
  if (!declared) throw new Error(`no contract entry for surface: ${surface}`)
  return DATE_NOUN[declared.date_column] || 'Acquired'
}

/** Single source for the country list — it used to be copy-pasted per modal. */
export const COUNTRIES = [
  {value: 'US', label: 'United States'},
  {value: 'CA', label: 'Canada'},
]

/* ------------------------------------------------------------------ */
/* The registry                                                        */
/* ------------------------------------------------------------------ */

/**
 * `type` picks the control FilterModal renders. `source` names the store-backed
 * option list for a multi-select. `chipItem` labels one selected item in the
 * bar; `itemTitle` labels one option in the dropdown — they differ on purpose
 * (a tag chips as its full hierarchical name, but picks by its own name).
 */
export const FIELDS = {
  fromDt: {
    type: 'date',
    label: (surface) => `${dateNoun(surface)} Date From`,
    cols: 4,
    default: null,
    chip: (value, surface) => (value ? `${dateNoun(surface)} From Date - ${value}` : null),
  },
  thruDt: {
    type: 'date',
    label: (surface) => `${dateNoun(surface)} Date Thru`,
    cols: 4,
    default: null,
    chip: (value, surface) => (value ? `${dateNoun(surface)} Thru Date - ${value}` : null),
  },

  onlyUntagged: {
    type: 'checkbox',
    label: () => 'Only show untagged images',
    cols: 4,
    default: true,
    // Default-on, so chipping it would put two chips on every untouched page.
    chip: () => null,
  },
  includePartiallyTagged: {
    type: 'checkbox',
    label: () => 'Include partially tagged',
    cols: 4,
    default: true,
    disabledWhen: (filter) => !filter.onlyUntagged,
    chip: () => null,
  },
  onlyNeedsReview: {
    type: 'checkbox',
    label: () => 'Tagger marked as questionable',
    cols: 4,
    default: false,
    chip: (value) => (value === true ? 'Only Needs Review' : null),
  },
  showQCed: {
    type: 'checkbox',
    label: () => 'Show items already QCed',
    cols: 4,
    default: false,
    chip: (value) => (value === true ? 'Show QCed' : null),
  },
  includeInactive: {
    type: 'checkbox',
    label: () => 'Include inactive photos',
    cols: 4,
    default: false,
    chip: (value) => (value === true ? 'Include Inactive Photos' : null),
  },

  tags: {
    type: 'multi',
    label: () => 'Tags',
    cols: 8,
    default: () => [],
    source: 'tags',
    // The Tag Category select above this control narrows the options; it is a
    // picking aid, not a criterion, so it has no CRITERIA entry of its own.
    categoryFilter: true,
    itemText: 'name',
    itemTitle: (item) => item.name,
    chipItem: (item) => item.full_name || item.name,
  },
  taggers: {
    type: 'multi',
    label: () => 'Taggers',
    cols: 4,
    default: () => [],
    source: 'users',
    itemText: 'email',
    itemTitle: (item) => item.email,
    chipItem: (item) => item.email,
  },
  projects: {
    type: 'multi',
    label: () => 'Projects',
    cols: 6,
    default: () => [],
    source: 'projects',
    itemText: 'name',
    itemTitle: (item) => `${item.id} - ${item.name}`,
    chipItem: (item) => item.name,
  },
  jobs: {
    type: 'multi',
    label: () => 'Jobs',
    cols: 6,
    default: () => [],
    source: 'jobs',
    itemText: 'name',
    itemTitle: (item) => `${item.id} - ${item.name}`,
    chipItem: (item) => item.name,
  },

  respIdSearchFreeForm: {
    type: 'text',
    label: () => 'Mass Response ID Search',
    hint: 'Photo response_id values. Space- or comma-separated.',
    cols: 4,
    default: null,
    // QCPhotoFilter split this to an array via .match(/\d+/g) and read it back
    // with .toString(). Three of the four builders call re.findall on the raw
    // value and raise on a list, so the string is the only type that works
    // everywhere; parse_id_search normalises both, which is why this is a
    // type cleanup and not a behaviour change.
    coerce: (value) => (Array.isArray(value) ? value.join(', ') : value),
    chip: (value) => (value ? `Response ID: ${value}` : null),
  },
  rgidSearchFreeForm: {
    type: 'text',
    label: () => 'Response Group ID Search',
    hint: 'Store-visit RGIDs (photo.response_group_id). Space- or comma-separated.',
    cols: 4,
    default: null,
    coerce: (value) => (Array.isArray(value) ? value.join(', ') : value),
    chip: (value) => (value ? `Response Group ID: ${value}` : null),
  },
  question_id: {
    type: 'text',
    label: () => 'Question ID',
    hint: 'Find photos for a specific question_id.',
    cols: 4,
    default: null,
    chip: (value) => (value ? `Question ID: ${value}` : null),
  },

  retailer: {
    type: 'select',
    label: () => 'Retailer',
    cols: 4,
    default: null,
    choicesProp: 'retailerChoices',
    loadingKey: 'get_retailer_list',
    chip: (value) => (value ? `Retailer: ${value}` : null),
  },
  department: {
    type: 'select',
    label: () => 'Department',
    cols: 4,
    default: null,
    choicesProp: 'departmentChoices',
    loadingKey: 'get_department_list',
    chip: (value) => (value ? `Department: ${value}` : null),
  },
  country: {
    type: 'country',
    label: () => 'Country',
    cols: 4,
    default: null,
    chip: (value) => (value ? `Country: ${value}` : null),
  },
}

/**
 * Render order: when, what state, what it is, which ids, where.
 *
 * Chip order follows this too, so the bar reads the same way the form does.
 */
export const ORDER = [
  'fromDt', 'thruDt',
  'onlyUntagged', 'includePartiallyTagged', 'onlyNeedsReview', 'showQCed', 'includeInactive',
  'tags', 'taggers', 'projects', 'jobs',
  'respIdSearchFreeForm', 'rgidSearchFreeForm', 'question_id',
  'retailer', 'department', 'country',
]

/** A field's value, normalised to the type every builder accepts. */
export function coerceValue(key, value) {
  const coerce = (FIELDS[key] || {}).coerce
  return typeof coerce === 'function' ? coerce(value) : value
}

/** The fields a surface shows, in order, minus anything the view owns. */
export function fieldsFor(surface, pageLevel = []) {
  // Loudly, and at construction: `supportedCriteria` answers an unknown surface
  // with every criterion, so a typo'd `surface=` prop would render the whole
  // form and quietly send keys the endpoint ignores.
  if (!CONTRACT_KEY[surface]) throw new Error(`unknown filter surface: ${surface}`)
  const allowed = supportedCriteria(surface)
  return ORDER
    .filter((key) => allowed.includes(key) && !pageLevel.includes(key))
    .map((key) => ({key, ...FIELDS[key], label: FIELDS[key].label(surface)}))
}

/** Starting value for every field a surface shows. */
export function defaultsFor(surface, pageLevel = []) {
  const out = {}
  fieldsFor(surface, pageLevel).forEach(({key, default: value}) => {
    out[key] = typeof value === 'function' ? value() : value
  })
  return out
}

/* A field spec for a criterion nobody can set is a filter nobody can reach, and
 * a spec for a criterion no serialiser reads is a control wired to nothing.
 * Both are caught by filter-unified.spec.js; this keeps the lists adjacent. */
export const UNSPECIFIED_CRITERIA = CRITERIA.filter((key) => !(key in FIELDS))
